import Service, { service } from '@ember/service'
import type Store from '@ember-data/store'
import type SessionService from 'teacher-ui-client/services/session'
import type SchoolRollover from 'teacher-ui-client/models/school-rollover'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { subDays, isBefore } from 'date-fns'
import { maxBy } from 'lodash'
import StepUtils from 'teacher-ui-client/school-rollover/step-utils'
import { RolloverStep } from 'teacher-ui-client/school-rollover/steps'
import { STATUS_ACTIVE, STATUS_COMPLETE } from 'teacher-ui-client/models/school-rollover'
import { RolloverState } from 'teacher-ui-client/school-rollover/manage-rollover-states'

export default class SchoolRolloverService extends Service {
  @service store!: Store

  @service session!: SessionService

  private get currentTeacher() {
    return this.session.currentTeacher
  }

  private get rollovers() {
    return this.store.peekAll<SchoolRollover>('school-rollover')
  }

  get steps() {
    return [
      RolloverStep.Teachers,
      RolloverStep.Students,
      RolloverStep.DeleteClasses,
      RolloverStep.DeleteStudents,
      RolloverStep.Finish,
    ]
  }

  fetchRollovers() {
    return this.store.query<SchoolRollover>('school-rollover', {})
  }

  /**
   * If the current teacher is a subco for the current subscription type, return the active
   * rollover record if one exists.
   *
   * It does not need to be the same subco that started the rollover.
   *
   * @returns {SchoolRolloverModel|undefined|null}
   */
  activeRolloverForSubco(subscriptionType: SubscriptionType) {
    if (!this.currentTeacher) return null

    const isSubco = this.currentTeacher.subcoFor(subscriptionType)
    if (!isSubco) return null

    return this.activeRollovers.find((rollover: SchoolRollover) => rollover.subscriptionType === subscriptionType)
  }

  get completedRollovers(): SchoolRollover[] {
    return this.rollovers.filter((rollover) => rollover.status === STATUS_COMPLETE)
  }

  get activeRollovers(): SchoolRollover[] {
    return this.rollovers.filter((rollover) => rollover.status === STATUS_ACTIVE)
  }

  get recentRollovers() {
    const recent = subDays(new Date(), 30)
    return this.completedRollovers.filter((schoolRollover) => isBefore(recent, schoolRollover.updatedAt))
  }

  get latestRecentRollover() {
    return maxBy(this.recentRollovers, (r) => r.updatedAt)
  }

  get readingRollovers(): SchoolRollover[] {
    return this.activeRollovers.filter((rollover) => rollover.subscriptionType === SubscriptionType.Reading)
  }

  get mathsRollovers(): SchoolRollover[] {
    return this.activeRollovers.filter((rollover) => rollover.subscriptionType === SubscriptionType.Maths)
  }

  get anyReadingRollovers() {
    return this.readingRollovers.length > 0
  }

  get anyMathsRollovers() {
    return this.mathsRollovers.length > 0
  }

  get readingRolloverState() {
    if (this.anyMathsRollovers) {
      return RolloverState.Locked
    } else if (this.anyReadingRollovers) {
      return RolloverState.InProgress
    } else {
      return RolloverState.Open
    }
  }

  get mathsRolloverState() {
    if (this.anyReadingRollovers) {
      return RolloverState.Locked
    } else if (this.anyMathsRollovers) {
      return RolloverState.InProgress
    } else {
      return RolloverState.Open
    }
  }

  // The route to go to if a subco wishes to continue their rollover
  continueRolloverRouteName(subscriptionType: SubscriptionType) {
    const rollover = this.activeRolloverForSubco(subscriptionType)
    const canTransitionToStep = rollover && this.canTransitionToStep(rollover, rollover.step)

    return canTransitionToStep ? StepUtils.getRouteNameForStep(rollover.step) : null
  }

  /**
   * Start a school rollover.
   */
  async start(subscriptionType: SubscriptionType) {
    return this.store
      .createRecord<SchoolRollover>('school-rollover', {
        step: RolloverStep.Teachers,
        subscriptionType,
      })
      .save()
  }

  /**
   * @param {SchoolRolloverModel} rollover
   */
  async complete(rollover: SchoolRollover) {
    if (rollover?.step !== RolloverStep.Finish)
      throw Error(
        `SchoolRolloverService.complete(rollover) was passed rollover with step '${rollover.step}' expected step '${RolloverStep.Finish}'`,
      )
    rollover.status = STATUS_COMPLETE
    return rollover.save()
  }

  /* Step logic */

  /**
   * @param {String} stepId
   * @returns {String|undefined}
   */
  getPreviousStep(stepId: RolloverStep) {
    return StepUtils.getPreviousStep(stepId, this.steps)
  }

  /**
   * @param {String} stepId
   * @returns {String|undefined}
   */
  getNextStep(stepId: RolloverStep) {
    return StepUtils.getNextStep(stepId, this.steps)
  }

  /**
   *
   * @param {SchoolRolloverModel} rollover
   * @param {String} step
   */
  async saveRolloverStepIfNew(rollover: SchoolRollover, step: RolloverStep) {
    const oldStep = StepUtils.isRolloverStepBeforeOrEqualTo(step, rollover.step, this.steps)
    if (oldStep) return
    rollover.step = step
    return rollover.save()
  }

  /**
   * @param {SchoolRolloverModel} rollover
   * @param {String} step
   * @returns {Boolean} true if the step is before/equal to or next from the furthest step recorded.
   */
  canTransitionToStep(rollover: SchoolRollover, step: RolloverStep) {
    const lastRecordedStep = rollover?.step

    if (!rollover) return false
    if (!lastRecordedStep) return false
    if (!rollover.isActive) return false

    return StepUtils.isStepBeforeEqualToOrNext(step, lastRecordedStep, this.steps)
  }
}

declare module '@ember/service' {
  interface Registry {
    'school-rollover': SchoolRolloverService
  }
}
